import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { Operator } from '../../../common/components/FilterPanel/enum/Operator';
import { StringExpression } from '../../../common/components/FilterPanel/types';
import { ProfileType, getProfileTypeLabel } from '../../../common/enums/ProfileType';
import { Preset } from '../../../common/models/valueObjects/Preset';
import { BaseGQLService } from '../../../common/services/BaseGQLService';

export const TAGS_QUERY = `
query($name: String, $first: Int){
  viewer {
    organization {
      slotTags(name: $name, first: $first)
    }
  }
}`;

export const PROJECTS_QUERY = `
query($filters: ProjectsFilter, $first: Int){
  viewer {
    organization {
      projects(filters: $filters first: $first sorters: [{field: name}]) {
        edges {
          node {
            id
            name
          }
        }
      }

    }
  }
}`;

type ProjectNode = RelayNode<{
  id: string;
  name: string;
}>;

export const PROFILES_QUERY = `
query($filters: ProfilesFilter, $first: Int){
  viewer {
    organization {
      profiles(filters: $filters first: $first sorters: [{field: name}]) {
        edges {
          node {
            id
            name
            type
          }
        }
      }

    }
  }
}`;

export const HAS_UNLIMITED_REPORTS_QUERY = `
query {
  viewer {
    organization {
      unlimitedReports
    }
  }
}
`;

type ProfileNode = RelayNode<{
  id: string;
  name: string;
  type: string;
}>;

export class OrganizationDataProvider extends BaseGQLService {
  async getTags(name: string, exclude: string[] = [], limit = 10): Promise<Preset[]> {
    // There is no backend filter to exclude tags from search.
    // If exclusions are provided, we fetch all (100_000) and evaluate them at runtime
    return this.query<Preset[]>(TAGS_QUERY, { name, first: exclude.length === 0 ? limit : 100_000 }, (data) => {
      const slotTags = (data?.viewer?.organization?.slotTags || []) as string[];

      if (exclude.length === 0) {
        return slotTags.map((tag: string) => new Preset(tag, tag));
      }

      return slotTags
        .filter((tag) => !exclude.includes(tag))
        .map((tag: string) => new Preset(tag, tag))
        .slice(0, limit);
    });
  }

  async getProjects(name: string, exclude: string[] = [], limit = 10): Promise<Preset[]> {
    const variables: Record<string, object | number> = { first: limit };
    const filters = this.buildFilter(name, exclude, 'name');
    if (filters) {
      variables.filters = filters;
    }

    return this.query<Preset[]>(PROJECTS_QUERY, variables, (data) => {
      const projects = (data?.viewer?.organization?.projects?.edges || []) as ProjectNode[];

      return projects.map(({ node: { id, name } }) => new Preset(id, name));
    });
  }

  async getProfiles(name: string, exclude: string[] = [], limit = 10): Promise<Preset[]> {
    const variables: Record<string, object | number> = { first: limit };
    const filters = this.buildFilter(name, exclude, 'name');
    if (filters) {
      variables.filters = filters;
    }

    return this.query<Preset[]>(PROFILES_QUERY, variables, (data) => {
      const profiles = (data?.viewer?.organization?.profiles?.edges || []) as ProfileNode[];

      return profiles.map(({ node: { id, name, type } }) => new Preset(id, name, getProfileTypeLabel(type as ProfileType)));
    });
  }

  public hasUnlimitedReports(): Promise<boolean> {
    return this.query(HAS_UNLIMITED_REPORTS_QUERY, {}, (data) => {
      return data.viewer.organization.unlimitedReports || false;
    });
  }

  private buildFilter(contains: string, neq: string[], field: string) {
    const filters = { operator: Operator.AND, subFilters: [] as Omit<StringExpression, 'id'>[] };
    if (contains.trim()) {
      filters.subFilters.push({ field, operation: Operation.CONTAINS, value: contains.trim() });
    }
    for (const value of neq) {
      filters.subFilters.push({ field, operation: Operation.NEQ, value });
    }
    if (filters.subFilters.length === 0) {
      return null;
    }
    if (filters.subFilters.length === 1) {
      return filters.subFilters[0];
    }
    return filters;
  }
}
